import {chain} from '@genestack/ui';
import classNames from 'classnames';
import React from 'react';

import {
    BTBCategoryInfo,
    editedDocumentSource,
    editedWorkingCopySource,
    FindUsagesEditorEvent,
    historyPanelDocumentSource,
    NodeEditor
} from '../../../../../../webeditor/biokb-editor-adapters';
import {BelEditorWrapper} from '../../../../../components/editor';
import {PublishStatusNotification} from '../../../../../providers/notifications-center/notifications/publish';
import {useUserPermittedEditingDoc} from '../../../../../hooks/user/use-has-permission';
import {EditorFooter} from '../../../common/editor-footer';
import {useAddDocumentToCategoryCache} from '../../hooks/categories/use-add-document-to-category-cache';
import {useRemoveDocumentFromCategoryCache} from '../../hooks/categories/use-remove-document-from-category-cache';
import {useLastActionQuery} from '../../hooks/use-last-action-query';
import {usePublishWorkingCopyMutation} from '../../hooks/use-publish-working-copy-mutation';
import {useFetchWorkingCopyQuery} from '../../hooks/use-fetch-working-copy-query';
import {LastActionStatus, Status} from '../bottom-bar';
import {DocumentEditorHeader} from '../document-editor-header';
import {useDocumentTitle} from '../document-editor-header/document-editor-header';
import {EditorBackdrop} from '../editor-backdrop';

import styles from './editor.module.css';
import {isDocumentBeingEdited} from '../../../../../utils/document';
import {useUnpublishedDocsQueryCache} from '../../hooks/use-unpublished-docs-query';
import {useDocumentQueryCache} from '../../hooks/use-document-query';
import {useFindUsagesQueryCache} from '../../sidebar/search-layout/hooks/use-find-usages-infinite-query';
import {useSearchDocsCache} from '../../sidebar/search-layout/hooks/use-edit-mode-search-query-cache';
import {useOntologyDocsQueryCache} from '../../hooks/use-ontology-docs-query';
import {showNotification} from '../../../../../providers/notifications-center/show-notification';

interface Props {
    isDocBeingEdited: boolean;
    document: EditModeDoc;
    focusedNodeId?: string;
    revisionId?: string;
    onNavigate: (accession: string | null, nodeId: string | null) => void;
    onFindUsages: (event: FindUsagesEditorEvent, editorInstance?: NodeEditor) => void;
    onLoadingStateChange: (loading: boolean) => void;
}

export function Editor(props: Props) {
    const [editorLoading, setEditorLoading] = React.useState(false);

    const editorRef = React.useRef<BelEditorWrapper>(null);
    const belEditorPublished = () => {
        editorRef.current?.onPublished(props.document.accession);
    };

    const publishWorkingCopyMutation = usePublishWorkingCopyMutation(belEditorPublished);
    const addDocumentToCategory = useAddDocumentToCategoryCache();
    const removeDocumentFromCategory = useRemoveDocumentFromCategoryCache();

    const unpublishedDocsCache = useUnpublishedDocsQueryCache();
    const documentQueryCache = useDocumentQueryCache();
    const findUsagesQueryCache = useFindUsagesQueryCache();
    const searchDocsQueryCache = useSearchDocsCache();
    const ontologyDocsQueryCache = useOntologyDocsQueryCache();

    const handleCurrentUserEditingStarted = React.useCallback(() => {
        if (isDocumentBeingEdited(props.document) && !props.document.editedByCurrentUser) {
            unpublishedDocsCache.updateDocument({...props.document, editedByCurrentUser: true});
            documentQueryCache.set({...props.document, editedByCurrentUser: true});
            findUsagesQueryCache.updateDocument({...props.document, editedByCurrentUser: true});
            searchDocsQueryCache.updateDocument({...props.document, editedByCurrentUser: true});
            ontologyDocsQueryCache.updateDocument({...props.document, editedByCurrentUser: true});
        }
    }, [
        props.document,
        unpublishedDocsCache,
        documentQueryCache,
        findUsagesQueryCache,
        searchDocsQueryCache,
        ontologyDocsQueryCache
    ]);

    const lastActionQuery = useLastActionQuery(
        {
            accession: props.document.accession,
            action: 'PUBLISH'
        },
        !props.isDocBeingEdited
    );

    const wcQuery = useFetchWorkingCopyQuery(
        {
            accession: props.document.accession
        },
        props.isDocBeingEdited
    );

    const handleAddCategory = ({accession, nodeId}: BTBCategoryInfo) => {
        const categoryId = `${accession}/${nodeId}`;
        alert('add category ' + categoryId);

        addDocumentToCategory.mutate({
            categoryId,
            document: props.document
        });
    };

    const handleRemoveCategory = ({accession, nodeId}: BTBCategoryInfo) => {
        const categoryId = `${accession}/${nodeId}`;

        removeDocumentFromCategory.mutate({
            categoryId,
            document: props.document
        });
    };

    const handlePublish = async (commitMessage?: string) => {
        try {
            await publishWorkingCopyMutation.mutateAsync({
                accession: props.document.accession,
                commitMessage
            });

            showNotification(<PublishStatusNotification />);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : undefined;

            showNotification(<PublishStatusNotification isError errorMessage={errorMessage} />);
        }
    };

    const isLoading = editorLoading || wcQuery.isInitialLoading;
    const documentTitle = useDocumentTitle(props.document, wcQuery.data?.workingCopyName);
    const userCanEditDocument = useUserPermittedEditingDoc(props.document);

    return (
        <div
            className={classNames(styles.editorContainer, {
                [styles.editorContainerViewMode]: !props.isDocBeingEdited
            })}
        >
            <DocumentEditorHeader
                document={props.document}
                workingCopyName={wcQuery.data?.workingCopyName}
                lastAction={lastActionQuery.data}
                onPublishSubmit={handlePublish}
                isRevision={!!props.revisionId}
            />

            <BelEditorWrapper
                documentSource={editViewDocumentSource(
                    props.document.accession,
                    documentTitle,
                    props.isDocBeingEdited,
                    userCanEditDocument,
                    props.revisionId,
                    wcQuery.data?.entityId
                )}
                onLoadingStateChange={chain(props.onLoadingStateChange, setEditorLoading)}
                focusedNodeId={props.focusedNodeId}
                onNavigate={props.onNavigate}
                onFindUsages={props.onFindUsages}
                onAddCategory={handleAddCategory}
                onRemoveCategory={handleRemoveCategory}
                ref={editorRef}
                onEditingStarted={handleCurrentUserEditingStarted}
            />

            {isLoading && <EditorBackdrop isDocumentLoading />}

            <EditorFooter>
                <Status>
                    {props.isDocBeingEdited ? (
                        'Draft'
                    ) : (
                        <LastActionStatus lastAction={lastActionQuery.data} />
                    )}
                </Status>
            </EditorFooter>
        </div>
    );
}

function editViewDocumentSource(
    accession: string,
    title: string,
    isDocBeingEdited: boolean,
    userCanEditDocument: boolean,
    revisionId?: string,
    entityId?: string
) {
    if (revisionId) {
        return historyPanelDocumentSource(accession, revisionId);
    }
    if (isDocBeingEdited && entityId) {
        return editedWorkingCopySource(accession, title, entityId, !userCanEditDocument);
    }
    if (!isDocBeingEdited) {
        return editedDocumentSource(accession, title);
    }
}
